import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Stack, TextField } from "@fluentui/react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Send28Filled, ImageAdd24Regular, Dismiss12Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import styles from "./QuestionInput.module.css";
import { SpeechInput } from "./SpeechInput";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";

interface Props {
    onSend: (question: string, images?: string[]) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    showSpeechInput?: boolean;
    showImageUpload?: boolean;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, initQuestion, showSpeechInput, showImageUpload }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const [images, setImages] = useState<string[]>([]);
    const { loggedIn } = useContext(LoginContext);
    const { t } = useTranslation();
    const [isComposing, setIsComposing] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    const addImageFromFile = useCallback((file: File) => {
        if (!file.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            setImages(prev => [...prev, base64]);
        };
        reader.readAsDataURL(file);
    }, []);

    const handlePaste = useCallback(
        (ev: React.ClipboardEvent) => {
            const items = ev.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.startsWith("image/")) {
                    const file = items[i].getAsFile();
                    if (file) addImageFromFile(file);
                }
            }
        },
        [addImageFromFile]
    );

    const handleDrop = useCallback(
        (ev: React.DragEvent) => {
            ev.preventDefault();
            const files = ev.dataTransfer?.files;
            if (!files) return;
            for (let i = 0; i < files.length; i++) {
                addImageFromFile(files[i]);
            }
        },
        [addImageFromFile]
    );

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question, images.length > 0 ? images : undefined);

        if (clearOnSend) {
            setQuestion("");
            setImages([]);
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (isComposing) return;

        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const handleCompositionStart = () => {
        setIsComposing(true);
    };
    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 1000) {
            setQuestion(newValue);
        }
    };

    const removeImage = (index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const disableRequiredAccessControl = requireLogin && !loggedIn;
    const sendQuestionDisabled = disabled || !question.trim() || requireLogin;

    if (disableRequiredAccessControl) {
        placeholder = "Please login to continue...";
    }

    return (
        <div onPaste={handlePaste} onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
            {images.length > 0 && (
                <Stack horizontal wrap tokens={{ childrenGap: 4 }} style={{ padding: "4px 8px" }}>
                    {images.map((img, i) => (
                        <div key={i} style={{ position: "relative", display: "inline-block" }}>
                            <img src={img} alt="Upload preview" style={{ height: 48, borderRadius: 4, border: "1px solid #ccc" }} />
                            <Button
                                size="small"
                                icon={<Dismiss12Regular />}
                                onClick={() => removeImage(i)}
                                style={{ position: "absolute", top: -4, right: -4, minWidth: 16, minHeight: 16, padding: 0, background: "#fff", borderRadius: "50%" }}
                            />
                        </div>
                    ))}
                </Stack>
            )}
            <Stack horizontal className={styles.questionInputContainer}>
                <TextField
                    className={styles.questionInputTextArea}
                    disabled={disableRequiredAccessControl}
                    placeholder={placeholder}
                    multiline
                    resizable={false}
                    borderless
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                />
                <div className={styles.questionInputButtonsContainer}>
                    {showImageUpload && (
                        <>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) addImageFromFile(file);
                                    e.target.value = "";
                                }}
                            />
                            <Tooltip content="Attach screenshot" relationship="label">
                                <Button size="large" icon={<ImageAdd24Regular />} onClick={() => fileInputRef.current?.click()} />
                            </Tooltip>
                        </>
                    )}
                    {showSpeechInput && <SpeechInput updateQuestion={setQuestion} />}
                    <Tooltip content={t("tooltips.submitQuestion")} relationship="label">
                        <Button size="large" icon={<Send28Filled primaryFill="rgba(33, 234, 112, 1)" />} disabled={sendQuestionDisabled} onClick={sendQuestion} />
                    </Tooltip>
                </div>
            </Stack>
        </div>
    );
};
